import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { toast } from 'react-toastify';
import useVacationRequestActions from '../../../hooks/APIHooks/users/useVacationRequestActions';
import { rejectVacationRequestSchema } from '../../../schemas';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaTextAreaField from '../Inputs/AcademiaTextAreaField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const VacationRequestRejectModal = ({
  handleModalClose,
  vacationRequestId,
}: {
  handleModalClose: () => void;
  vacationRequestId: number;
}) => {
  // integration of custom API hooks here
  const { approveOrRejectRequestHandler } = useVacationRequestActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to handle rejecting vacation request declared here
  const rejectVacationRequestHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      await approveOrRejectRequestHandler
        .mutateAsync({
          vacReqId: vacationRequestId,
          body: {
            is_approved: false,
            is_rejected: true,
            rejected_note: values.rejected_note,
          },
        })
        .then((res) => {
          if (res.is_success) {
            toast.success('Request Rejected!!');

            handleModalClose();
            queryClient.invalidateQueries({
              queryKey: ['vacation-request-list'],
            });
          } else {
            toast.error(res.message || 'Request Rejecting Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering vacation request reject modal component here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText='Are you sure?' />

      <p className='text-left whitespace-pre-wrap text-base-dark-blue'>
        Are you sure you want to reject this vacation request?
      </p>

      <Formik
        initialValues={{ rejected_note: '' }}
        validationSchema={rejectVacationRequestSchema}
        onSubmit={rejectVacationRequestHandler}
      >
        {(props) => (
          <Form>
            <AcademiaTextAreaField
              name='rejected_note'
              label='Comments'
              placeholder='Mention your reasons for the action here...'
              isRequired
            />

            <div className='flex items-center justify-center md:justify-end gap-3 !mt-5'>
              <AcademiaButton
                type='submit'
                variant='danger'
                customWidth={150}
                isLoading={props.isSubmitting}
              >
                Yes
              </AcademiaButton>

              <AcademiaButton
                onClick={handleModalClose}
                type='button'
                customWidth={150}
                variant='success'
              >
                No
              </AcademiaButton>
            </div>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default VacationRequestRejectModal;
